/**
 * Web-font bootstrap. Loads the curator's bundled stylesheet `public/assets/fonts/fonts.css`
 * (families "Nantian Sans" 400/700, "Nantian Serif" 700, "Nantian Display" 400 — OFL subsets of
 * Noto Sans SC / Noto Serif SC / ZCOOL XiaoWei) and resolves once the browser has the faces (or gave
 * up), so Phaser Text measures with the final glyphs. Missing faces degrade to the system fallback
 * stack declared in `@/core/config` and are reported through `getFontStatus()` plus one console.warn.
 */
import { FONT_BODY as CONFIG_BODY, FONT_DISPLAY as CONFIG_DISPLAY, FONT_TITLE as CONFIG_TITLE } from '@/core/config';

export interface FontFace { family: string; weight: number; file: string }

/** Faces declared by public/assets/fonts/fonts.css (kept in sync with the curator's build). */
export const FONT_FACES: FontFace[] = [
  { family: 'Nantian Sans', weight: 400, file: 'noto-sans-sc-regular.woff2' },
  { family: 'Nantian Sans', weight: 700, file: 'noto-sans-sc-bold.woff2' },
  { family: 'Nantian Serif', weight: 700, file: 'noto-serif-sc-bold.woff2' },
  { family: 'Nantian Display', weight: 400, file: 'zcool-xiaowei-regular.woff2' },
];
/** @deprecated alias kept for older imports. */
export const FONT_FILES = FONT_FACES;

/** Body copy (Nantian Sans → system CJK sans). */
export const FONT_BODY = CONFIG_BODY;
/** Calligraphic display face (Nantian Display → Nantian Serif → system serif). */
export const FONT_DISPLAY = CONFIG_DISPLAY;
/** Serif for chapter headings / letter body (Nantian Serif → system serif). */
export const FONT_TITLE = CONFIG_TITLE;
/** @deprecated alias of FONT_TITLE. */
export const FONT_SERIF = CONFIG_TITLE;

export interface FontStatus { attempted: boolean; loaded: string[]; missing: string[]; usingFallback: boolean }

const status: FontStatus = { attempted: false, loaded: [], missing: [], usingFallback: false };
let pending: Promise<void> | null = null;

const LINK_ID = 'nantian-fonts-css';
const STYLE_ID = 'nantian-fonts';
const TEST_STRING = '南天传说南洋行';

function baseUrl(): string {
  const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? './';
  return base.endsWith('/') ? base : `${base}/`;
}

export function fontUrl(file: string): string {
  return `${baseUrl()}assets/fonts/${file}`;
}

export function fontsCssUrl(): string {
  return `${baseUrl()}assets/fonts/fonts.css`;
}

/**
 * Idempotently attaches the bundled stylesheet. Resolves when it loaded, or when it failed (then the
 * inline @font-face fallback is injected so a broken relative URL still gets a chance). No-op outside
 * a browser.
 */
export function injectFontFaces(timeoutMs = 3000): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  const existing = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  if (existing) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    link.href = fontsCssUrl();
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      if (!ok) injectInlineFaces();
      resolve();
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    link.addEventListener('load', () => finish(true));
    link.addEventListener('error', () => finish(false));
    document.head.appendChild(link);
  });
}

/** Inline @font-face rules mirroring fonts.css (used when the stylesheet cannot be fetched). */
export function injectInlineFaces(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const css = FONT_FACES.map((f) =>
    `@font-face{font-family:"${f.family}";font-weight:${f.weight};font-style:normal;font-display:block;src:url("${fontUrl(f.file)}") format("woff2");}`,
  ).join('\n');
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = css;
  document.head.appendChild(el);
}

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, () => { clearTimeout(t); resolve(fallback); });
  });
}

/** `document.fonts` shape we rely on (typed loosely so Node tests can stub it). */
interface FontFaceSetLike { load(spec: string, text?: string): Promise<{ status: string }[]> }

/**
 * Resolves when every bundled face is either loaded or known to be missing (never rejects, never
 * hangs longer than ~`timeoutMs`). The integrator awaits this in Preload before the first Text.
 */
export function ensureFonts(timeoutMs = 4000): Promise<void> {
  if (pending) return pending;
  pending = (async () => {
    status.attempted = true;
    const fonts = (typeof document !== 'undefined' ? (document as unknown as { fonts?: FontFaceSetLike }).fonts : undefined) ?? null;
    if (!fonts) { status.usingFallback = true; status.missing = FONT_FACES.map((f) => `${f.family} ${f.weight}`); return; }
    await injectFontFaces(Math.min(timeoutMs, 3000));
    await Promise.all(FONT_FACES.map(async (f) => {
      const spec = `${f.weight} 16px "${f.family}"`;
      const faces = await withTimeout(fonts.load(spec, TEST_STRING), timeoutMs, [] as { status: string }[]);
      const ok = faces.length > 0 && faces.some((face) => face.status === 'loaded');
      const id = `${f.family} ${f.weight}`;
      if (ok) status.loaded.push(id); else status.missing.push(id);
    }));
    status.usingFallback = status.missing.length > 0;
    if (status.usingFallback) console.warn(`[ui/fonts] missing bundled fonts, using system fallback: ${status.missing.join(', ')}`);
  })();
  return pending;
}

export function getFontStatus(): FontStatus { return { ...status, loaded: [...status.loaded], missing: [...status.missing] }; }

/** Test hook. */
export function _resetFontsForTests(): void { pending = null; status.attempted = false; status.loaded = []; status.missing = []; status.usingFallback = false; }
